import { html, nothing, PropertyValueMap, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksElement } from '../BlocksElement';
import type { ImageGalleryItem, ImageGalleryItemGridPosition, ImageGalleryItemObjectFit } from '../image-gallery-item/image-gallery-item.model';
import styles from './image-gallery-item.styles';

/**
 * @slot - The component content
 * @csspart base - The component's base wrapper.
 * @csspart image - The component's image for image item
 * @csspart iframe - The component's iframe for video item.
 *  @csspart part-default - The component's default slot.
 */

export class BlocksImageGalleryItem extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Indicates if the panel is currently active.
   */
  @property({ type: Boolean, reflect: true })
  isActive = false;

  /**
   * state to maintain selected image
   */
  @property({ type: Object }) data: ImageGalleryItem | undefined;

  @property({ type: String })
  objectFit: ImageGalleryItemObjectFit;

  /**
   * Grid position prop
   */
  @property({ type: String })
  gridPosition: ImageGalleryItemGridPosition = 'left';

  /**
   *  ariaroledescription for customized label announcements for tab panel
   */
  @property({ type: String })
  ariaroledescription: string | undefined;

  /**
   * arialabel for customized label announcements for tab panel
   */
  @property({ type: String })
  arialabel: string | undefined;

  protected slots = new SlotController(this, null);

  /**
   * Lifecycle method called after the element's DOM has been updated the first time.
   * Sets the role attribute to 'tabpanel' and tabindex attribute to '-1'.
   * This method is typically used to perform one-time initialization tasks.
   */
  protected firstUpdated(): void {
    this.setAttribute('role', 'tabpanel');
    this.setAttribute('tabindex', '-1');
  }

  /**
   * Called when the element's properties are updated.
   *
   * @param _changedProperties - A map of the properties that have changed.
   *
   * This method checks if the 'ariaroledescription' or 'arialabel' properties have changed.
   * If they have, and they are not null or undefined, it sets the corresponding ARIA attributes
   * ('aria-roledescription' and 'aria-label') on the element.
   */
  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('ariaroledescription') && this.ariaroledescription) {
      this.setAttribute('aria-roledescription', this.ariaroledescription);
    }
    if (_changedProperties.has('arialabel') && this.arialabel) {
      this.setAttribute('aria-label', this.arialabel);
    }
  }

  private renderThumbnails() {
    return html`
      ${this.data?.type !== 'video'
        ? html`
            <img part="image" class="cat-c-image-gallery__img" src=${ifDefined(this.data?.src)} alt=${ifDefined(this.data?.alt)} loading="lazy" />
          `
        : html`
            <iframe part="iframe" class="cat-c-image-gallery__iframe" src=${this.data?.src + '?controls=1&embedded=true'} loading="lazy"> </iframe>
          `}
    `;
  }

  render() {
    const componentClassNames = this.componentClassNames('cat-c-image-gallery-item ', {
      'cat-c-image-gallery-item--selected': this.isActive,
      'cat-c-image-gallery--right': this.gridPosition === 'right',
      'cat-c-image-gallery--bottom': this.gridPosition === 'bottom',
      'cat-c-image-gallery--cover': this.objectFit === 'cover',
      'cat-c-image-gallery--fill': this.objectFit === 'fill',
      'cat-c-image-gallery--none': this.objectFit === 'none',
      'cat-c-image-gallery-item--has-slots': !this.slots.isEmpty() && this.isActive
    });

    return html`
      <div part="base" class="${componentClassNames}">
        ${this.slots.isEmpty() ? this.renderThumbnails() : nothing}
        <slot part="part-default"></slot>
      </div>
    `;
  }
}

if (customElements.get('cat-image-gallery-item') === undefined) {
  customElements.define('cat-image-gallery-item', BlocksImageGalleryItem);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-image-gallery-item': BlocksImageGalleryItem;
  }
}
